import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiClock,
  FiHelpCircle,
  FiPackage,
  FiShield,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import routes from "../routes";

const Policy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const faqItems = [
    {
      q: t("policyPage.faq.shippingQuestion", "How long does shipping take?"),
      a: t(
        "policyPage.faq.shippingAnswer",
        "Orders are usually dispatched within 1-2 business days. Delivery timing depends on destination and carrier workload.",
      ),
    },
    {
      q: t("policyPage.faq.trackingQuestion", "Can I track my order?"),
      a: t(
        "policyPage.faq.trackingAnswer",
        "Yes. Once shipped, you receive tracking details so you can follow your delivery progress.",
      ),
    },
    {
      q: t(
        "policyPage.faq.issueQuestion",
        "What if there is an issue with my order?",
      ),
      a: t(
        "policyPage.faq.issueAnswer",
        "If an item arrives damaged or incorrect, contact us within 48 hours and we will help resolve it quickly.",
      ),
    },
    {
      q: t("policyPage.faq.paymentQuestion", "Are my payment details secure?"),
      a: t(
        "policyPage.faq.paymentAnswer",
        "Yes. Checkout uses secure payment processing and encrypted communication for your safety.",
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#f7f0ec] pt-24 pb-12 px-4 sm:px-6 lg:px-8 font-raleway">
      <div className="mx-auto max-w-5xl">
        <button
          type="button"
          onClick={() => navigate(routes.home)}
          className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-[#7e3957] hover:text-[#5d2842]"
        >
          <FiArrowLeft />
          {t("policyPage.backHome", "Back to Home")}
        </button>

        <div className="overflow-hidden rounded-[2rem] border border-[#ead4d8] bg-white shadow-[0_30px_80px_-55px_rgba(109,58,74,0.45)]">
          <div className="bg-[linear-gradient(145deg,#fff7f8_0%,#fffefb_55%,#fdf1ed_100%)] px-6 py-10 sm:px-10">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#b17075]">
              {t("policyPage.hero.badge", "Policy Center")}
            </p>
            <h1 className="mt-3 text-3xl sm:text-4xl font-black text-[#7e3957]">
              {t(
                "policyPage.hero.title",
                "Shipping, Returns, and Customer Support",
              )}
            </h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base leading-7 text-[#6f5b64]">
              {t(
                "policyPage.hero.description",
                "We believe trust comes from clarity. Here is a simple overview of how we handle shipping, order support, and checkout safety.",
              )}
            </p>
          </div>

          <div className="grid gap-4 p-6 sm:grid-cols-3 sm:p-8">
            <div className="rounded-2xl border border-[#f1e2e7] bg-[#fffafb] p-5">
              <FiPackage className="text-2xl text-[#83375B]" />
              <h3 className="mt-3 font-bold text-[#7e3957]">
                {t("policyPage.cards.shipping.title", "Shipping")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                {t(
                  "policyPage.cards.shipping.description",
                  "Dispatch in 1-2 business days for in-stock products, with tracking updates after shipment.",
                )}
              </p>
            </div>
            <div className="rounded-2xl border border-[#f1e2e7] bg-[#fffafb] p-5">
              <FiClock className="text-2xl text-[#83375B]" />
              <h3 className="mt-3 font-bold text-[#7e3957]">
                {t("policyPage.cards.support.title", "Support Window")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                {t(
                  "policyPage.cards.support.description",
                  "For delivery or product issues, contact us as soon as possible so we can assist quickly.",
                )}
              </p>
            </div>
            <div className="rounded-2xl border border-[#f1e2e7] bg-[#fffafb] p-5">
              <FiShield className="text-2xl text-[#83375B]" />
              <h3 className="mt-3 font-bold text-[#7e3957]">
                {t("policyPage.cards.checkout.title", "Secure Checkout")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                {t(
                  "policyPage.cards.checkout.description",
                  "Payments are processed through secure providers and encrypted communication channels.",
                )}
              </p>
            </div>
          </div>

          <div className="border-t border-[#f0e2e7] p-6 sm:p-8">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#f8ebf0] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#7e3957]">
              <FiHelpCircle />
              {t("policyPage.faq.badge", "FAQ")}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {faqItems.map((item) => (
                <div
                  key={item.q}
                  className="rounded-xl border border-[#f1e2e7] bg-white p-4"
                >
                  <h4 className="font-semibold text-[#7e3957]">{item.q}</h4>
                  <p className="mt-2 text-sm leading-6 text-[#6f5b64]">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Policy;
